import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './ImpactStory.css';

/* ================================================================
   DATA
   ================================================================ */
const stories = [
  {
    id: 1,
    tag: 'Fund Structuring',
    region: 'Southwest',
    accent: '#1B4620',
    accentLight: '#e8f0e8',
    title: 'Place-Based Affordable Housing Debt Fund',
    stat: '$100M',
    statLabel: 'Fund Size',
    img: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=900&q=80',
    challenge:
      'A community foundation wanted to catalyze affordable housing investment in its region but needed a vehicle that could blend philanthropic, public, and institutional capital into a single lending platform.',
    work: [
      'Designed a $100M pooled debt fund with six capital layers',
      'Built the financial model, waterfall mechanics, and investor materials',
      'Supported capital raise across municipal, philanthropic, and institutional investors',
    ],
    scope: ['Fund Design', 'Financial Modeling', 'Capital Raise', 'Waterfall Mechanics'],
  },
  {
    id: 2,
    tag: 'Fund Structuring',
    region: 'Northeast',
    accent: '#1B4620',
    accentLight: '#e8f0e8',
    title: 'Place-Based Impact Fund',
    stat: '$45M',
    statLabel: 'Fund Size',
    img: 'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=900&q=80',
    challenge:
      'Strong origination capacity but needed to move lending volume off the balance sheet to free up capital for continued growth.',
    work: [
      'Structured an off-balance sheet pooled fund with tiered investor returns',
      'Developed governance framework and investment committee structure',
      'Modeled fund economics across multiple deployment scenarios',
    ],
    scope: ['Off-Balance Sheet', 'Tiered Returns', 'Governance'],
  },
  {
    id: 3,
    tag: 'Fund Structuring',
    region: 'National',
    accent: '#1B4620',
    accentLight: '#e8f0e8',
    title: 'Catalytic Capital Fund',
    stat: '$75M',
    statLabel: 'Fund Size',
    img: 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=900&q=80',
    challenge:
      'Needed to design a fund that could blend philanthropic and institutional capital to reach deeper-impact borrowers that conventional structures cannot serve.',
    work: [
      'Developed a blended capital stack with concessionary and market-rate tranches',
      'Created the community thesis, impact framework, and investor pitch',
      'Supported legal structuring and closing coordination',
    ],
    scope: ['Blended Capital', 'Below-Market Tranches', 'Impact Framework'],
  },
  {
    id: 4,
    tag: 'Treasury & Capital',
    region: 'Regional CDFI',
    accent: '#E67E45',
    accentLight: '#fdf0e8',
    title: 'Balance Sheet Optimization',
    stat: '$150M+',
    statLabel: 'AUM Analyzed',
    img: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=900&q=80',
    challenge:
      'Complex balance sheet with multiple capital sources, rising cost of funds, and limited visibility into how treasury decisions affected lending capacity.',
    work: [
      'Conducted a full balance sheet and cost-of-funds analysis',
      'Modeled capital structure scenarios to optimize the mix of capital',
      'Developed a treasury dashboard for ongoing monitoring',
    ],
    scope: ['Cost of Funds', 'Capital Structure', 'Treasury Dashboard'],
  },
  {
    id: 5,
    tag: 'Underwriting & Lending',
    region: 'National',
    accent: '#2a6b9e',
    accentLight: '#e8f0f8',
    title: 'Underwriting Model Redesign',
    stat: '4',
    statLabel: 'Regions',
    img: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=900&q=80',
    challenge:
      'Underwriting processes were inconsistent across regions, with no standardized credit memo format, scoring framework, or approval matrix.',
    work: [
      'Designed a standardized underwriting model with sector-specific criteria',
      'Built credit memo templates, scoring rubrics, and approval workflows',
      'Trained lending staff across multiple offices on the new framework',
    ],
    scope: ['Credit Policy', 'Memo Templates', 'Staff Training'],
  },
  {
    id: 6,
    tag: 'Capital Strategy',
    region: 'Regional CDFI',
    accent: '#6b3fa0',
    accentLight: '#f0e8f8',
    title: 'Participation & Leverage Strategy',
    stat: '20%',
    statLabel: 'Capacity Increase',
    img: 'https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=900&q=80',
    challenge:
      'Limited balance sheet capacity was constraining origination. Needed a strategy to bring in participation partners and leverage existing capital more effectively.',
    work: [
      'Mapped potential participation and syndication partners across the region',
      'Designed a participation strategy with standardized documentation',
      'Modeled impact on lending capacity, concentration limits, and capital ratios',
    ],
    scope: ['Syndication', 'Partner Docs', 'Capacity Modeling'],
  },
];

/* ================================================================
   HOOKS
   ================================================================ */

/** Detect mobile viewport (matches the CSS breakpoint) */
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    setIsMobile(mql.matches);
    return () => mql.removeEventListener('change', handler);
  }, [breakpoint]);

  return isMobile;
}

/**
 * useScrollElevator — converts scroll position within a tall wrapper
 * into a slide index. The wrapper is (slides × 100vh) tall so the
 * browser scrollbar reflects real progress.
 */
function useScrollElevator(wrapperRef, count, enabled = true) {
  const [active, setActive] = useState(0);
  const [direction, setDirection] = useState('down');
  const [progress, setProgress] = useState(0);
  const prevActive = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(() => {
        ticking = false;
        const rect = wrapper.getBoundingClientRect();
        const wrapperHeight = wrapper.scrollHeight || wrapper.offsetHeight;
        const viewportH = window.innerHeight;

        const scrolled = -rect.top;
        const scrollableDistance = wrapperHeight - viewportH;

        if (scrollableDistance <= 0) return;

        const raw = scrolled / scrollableDistance;
        const clamped = Math.max(0, Math.min(1, raw));

        setProgress(clamped);

        const idx = Math.min(
          count - 1,
          Math.max(0, Math.floor(clamped * count))
        );

        if (idx !== prevActive.current) {
          setDirection(idx > prevActive.current ? 'down' : 'up');
          prevActive.current = idx;
          setActive(idx);
        }
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [wrapperRef, count, enabled]);

  return { active, direction, progress };
}

/* ================================================================
   MOBILE CARD COMPONENT
   ================================================================ */
const MobileCard = ({ story, index, expandedId, onToggle }) => {
  const isExpanded = expandedId === story.id;

  return (
    <div
      className={`ix-m-card ${isExpanded ? 'ix-m-card-expanded' : ''}`}
      style={{ '--accent': story.accent, '--accent-light': story.accentLight }}
    >
      {/* Compact card header — always visible, tappable */}
      <button
        className="ix-m-card-header"
        onClick={() => onToggle(story.id)}
        aria-expanded={isExpanded}
      >
        <div className="ix-m-card-top">
          <span className="ix-m-card-num">{String(index + 1).padStart(2, '0')}</span>
          <span className="ix-m-card-tag">{story.tag}</span>
          <span className="ix-m-card-region">{story.region}</span>
        </div>
        <div className="ix-m-card-hero">
          <span className="ix-m-card-stat">{story.stat}</span>
          <span className="ix-m-card-stat-label">{story.statLabel}</span>
        </div>
        <h3 className="ix-m-card-title">{story.title}</h3>
        <span className={`ix-m-card-chevron ${isExpanded ? 'ix-m-card-chevron-open' : ''}`}>
          &#8250;
        </span>
      </button>

      {/* Expandable detail */}
      <div className={`ix-m-card-body ${isExpanded ? 'ix-m-card-body-open' : ''}`}>
        <p className="ix-m-card-challenge">{story.challenge}</p>

        <div className="ix-m-card-work">
          <span className="ix-m-card-work-label">What we did</span>
          <ul className="ix-m-card-work-list">
            {story.work.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>

        <div className="ix-m-card-scope">
          {story.scope.map((s, i) => (
            <span key={i} className="ix-m-card-pill">{s}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ================================================================
   DESKTOP COMPONENT
   ================================================================ */
const DesktopView = () => {
  const wrapperRef = useRef(null);
  const { active, direction, progress } = useScrollElevator(wrapperRef, stories.length, true);

  const story = stories[active];

  const filters = useMemo(() => {
    const tags = [...new Set(stories.map(s => s.tag))];
    return ['All', ...tags];
  }, []);

  const goTo = useCallback((idx) => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const wrapperTop = wrapper.getBoundingClientRect().top + window.scrollY;
    const scrollableDistance = wrapper.offsetHeight - window.innerHeight;
    const targetScroll = wrapperTop + (idx / (stories.length - 1)) * scrollableDistance;
    window.scrollTo({ top: targetScroll, behavior: 'smooth' });
  }, []);

  const goToFilter = useCallback((tag) => {
    if (tag === 'All') {
      goTo(0);
      return;
    }
    const idx = stories.findIndex(s => s.tag === tag);
    if (idx >= 0) goTo(idx);
  }, [goTo]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        if (active < stories.length - 1) goTo(active + 1);
      }
      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        if (active > 0) goTo(active - 1);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [active, goTo]);

  const slideProgress = (() => {
    const segSize = 1 / stories.length;
    const segStart = active * segSize;
    return Math.min(1, Math.max(0, (progress - segStart) / segSize));
  })();

  const activeFilter = story.tag;

  return (
    <div className="ix-scroll-wrapper" ref={wrapperRef} id="impact">
      <div className="ix">

        <div className="ix-wipe ix-wipe-top" />
        <div className="ix-wipe ix-wipe-bottom" />

        <div className="ix-mask">

          <div className="ix-left" style={{ '--accent': story.accent }}>
            <div className="ix-left-bg" key={`bg-${active}`} style={{ backgroundImage: `url(${story.img})` }} />
            <div className="ix-left-content">

              <div className="ix-filters">
                {filters.map((f) => (
                  <button
                    key={f}
                    className={`ix-filter-pill ${
                      f === 'All'
                        ? active === 0 && progress < 0.02 ? 'ix-filter-active' : ''
                        : f === activeFilter ? 'ix-filter-active' : ''
                    }`}
                    onClick={() => goToFilter(f)}
                  >
                    {f}
                  </button>
                ))}
              </div>

              <div className={`ix-hero ix-hero-${direction}`} key={active}>
                <span className="ix-stat">{story.stat}</span>
                <span className="ix-stat-label">{story.statLabel}</span>
                <h2 className="ix-title">{story.title}</h2>
                <div className="ix-meta">
                  <span className="ix-tag">{story.tag}</span>
                  <span className="ix-sep" />
                  <span className="ix-region">{story.region}</span>
                </div>
              </div>

              <div className="ix-left-footer">
                <span className="ix-counter">
                  {String(active + 1).padStart(2, '0')} / {String(stories.length).padStart(2, '0')}
                </span>
                <span className="ix-section-label">Selected Engagements</span>
              </div>
            </div>

            <div className="ix-slide-bar" style={{ '--slide-progress': `${slideProgress * 100}%` }} />
          </div>

          <div className="ix-right" style={{ '--accent': story.accent, '--accent-light': story.accentLight }}>

            <div className="ix-elevator-car">
              <div className={`ix-detail ix-detail-${direction}`} key={`detail-${active}`}>
                <p className="ix-challenge">{story.challenge}</p>

                <div className="ix-work-section">
                  <span className="ix-work-label">What we did</span>
                  <ul className="ix-work-list">
                    {story.work.map((item, i) => (
                      <li key={i} style={{ '--stagger': `${i * 80 + 200}ms` }}>{item}</li>
                    ))}
                  </ul>
                </div>

                <div className="ix-scope">
                  {story.scope.map((s, i) => (
                    <span key={i} className="ix-pill">{s}</span>
                  ))}
                </div>

                <button className="ix-cta" onClick={() => goTo(active < stories.length - 1 ? active + 1 : 0)}>
                  <span className="ix-cta-text">
                    {active < stories.length - 1 ? 'Next Engagement' : 'Back to First'}
                  </span>
                  <span className="ix-cta-circle">
                    <span className="ix-cta-arrow">&rsaquo;</span>
                  </span>
                </button>
              </div>
            </div>

            <div className="ix-edge-mask ix-edge-top" />
            <div className="ix-edge-mask ix-edge-bottom" />
          </div>

          <nav className="ix-nav" aria-label="Case study navigation">
            {stories.map((s, i) => (
              <button
                key={s.id}
                className={`ix-nav-dot ${i === active ? 'ix-nav-active' : ''}`}
                style={{ '--dot-accent': s.accent }}
                onClick={() => goTo(i)}
                aria-label={`Go to ${s.title}`}
              >
                <span className="ix-nav-num">{String(i + 1).padStart(2, '0')}</span>
              </button>
            ))}
            <div
              className="ix-nav-track"
              style={{ '--progress': `${progress * 100}%` }}
            />
          </nav>

          {active === 0 && (
            <div className="ix-scroll-hint">
              <span className="ix-scroll-hint-text">Scroll to explore</span>
              <span className="ix-scroll-hint-arrow">&darr;</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ================================================================
   MOBILE VIEW
   ================================================================ */
const MobileView = () => {
  const [expandedId, setExpandedId] = useState(null);
  const [activeFilter, setActiveFilter] = useState('All');

  const filters = useMemo(() => {
    const tags = [...new Set(stories.map(s => s.tag))];
    return ['All', ...tags];
  }, []);

  const filteredStories = useMemo(() => {
    if (activeFilter === 'All') return stories;
    return stories.filter(s => s.tag === activeFilter);
  }, [activeFilter]);

  const handleToggle = useCallback((id) => {
    setExpandedId(prev => prev === id ? null : id);
  }, []);

  const handleFilter = useCallback((tag) => {
    setActiveFilter(tag);
    setExpandedId(null); // collapse any open card when filtering
  }, []);

  return (
    <div className="ix-mobile" id="impact">
      {/* Section header */}
      <div className="ix-m-header">
        <span className="ix-m-eyebrow">Selected Engagements</span>
        <h2 className="ix-m-heading">Client Work</h2>
      </div>

      {/* Filter pills — horizontal scroll */}
      <div className="ix-m-filters">
        {filters.map((f) => (
          <button
            key={f}
            className={`ix-m-filter-pill ${f === activeFilter ? 'ix-m-filter-active' : ''}`}
            onClick={() => handleFilter(f)}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Cards */}
      <div className="ix-m-cards">
        {filteredStories.map((story, i) => (
          <MobileCard
            key={story.id}
            story={story}
            index={stories.indexOf(story)}
            expandedId={expandedId}
            onToggle={handleToggle}
          />
        ))}
      </div>

      {/* Counter */}
      <div className="ix-m-footer">
        <span className="ix-m-count">
          {filteredStories.length} engagement{filteredStories.length !== 1 ? 's' : ''}
        </span>
      </div>
    </div>
  );
};

/* ================================================================
   MAIN — switches between desktop and mobile
   ================================================================ */
const ImpactStory = () => {
  const isMobile = useIsMobile();

  return isMobile ? <MobileView /> : <DesktopView />;
};

export default ImpactStory;
